import { ColumnsType } from 'antd/es/table/interface';
import { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import SortArrow from '../assets/images/icons/SortArrow';
import { CountryAndPaymentMethod } from '../models/CountryAndPaymentMethod';

import {
	EyeOutlined,
	PlusOutlined,
	EditFilled,
	CheckCircleOutlined,
	CloseCircleOutlined,
	CaretUpOutlined,
	CaretDownOutlined,
} from '@ant-design/icons';

import { Tooltip } from 'antd';
import './countryAndPaymMethodColumn.scss';
import { handleSortClick } from './sorterColumn';
import { ColumnTitle } from '../components/ColumnTitle/ColumnTitle';

export const getCountryAndPaymMethodColumn = (
	t: TFunction<'translation', undefined, 'translation'>,
	sortBy: string,
	setSortBy: (sortValue: string) => void,
) => {
	const columns: ColumnsType<CountryAndPaymentMethod> = [
		{
			responsive: ['lg'],
			title: (
				<div>
					<Link to={`/add-country`}>
						<PlusOutlined className='flex items-center cursor-pointer text-black font-bold' />
					</Link>
				</div>
			),
			dataIndex: 'add',
			key: 'add',
			ellipsis: true,
			render: (value, record) => {
				return (
					<Link to={`/countries/${record.countryCode}`}>
						<EditFilled className='flex items-center cursor-pointer  text-black font-bold' />
					</Link>
				);
			},
		},
		{
			responsive: ['lg'],
			title: (
				<div className='  flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('name')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'name-asc'))}
							style={{ color: sortBy === 'name-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'name-desc'))}
							style={{ color: sortBy === 'name-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'name',
			key: 'name',
			ellipsis: true,
			render: (value, record) => {
				return <div className=' text-[#00142A] flex items-center  font-medium'>{record.countryName}</div>;
			},
		},
		{
			responsive: ['lg'],
			title: (
				<div className='flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('countryCode')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'country-code-asc'))}
							style={{ color: sortBy === 'country-code-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'country-code-desc'))}
							style={{ color: sortBy === 'country-code-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'country-code',
			key: 'country-code',
			render: (value, record) => {
				return <div className=' text-[#626A74] flex items-center font-medium'>{record.countryCode}</div>;
			},
		},
		{
			responsive: ['lg'],
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('currencyCode')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'currency-code-asc'))}
							style={{ color: sortBy === 'currency-code-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'currency-code-desc'))}
							style={{ color: sortBy === 'currency-code-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'currency-code',
			key: 'currency-code',
			render: (value, record) => (
				<div className=' text-[#626A74] flex items-center font-medium'>{record.currencyCode}</div>
			),
		},
		{
			responsive: ['lg'],
			title: (
				<div className='flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('maxProfitRatio')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'max-profit-asc'))}
							style={{ color: sortBy === 'max-profit-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'max-profit-desc'))}
							style={{ color: sortBy === 'max-profit-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'max-profit',
			key: 'max-profit',
			render: (value, record) => {
				return (
					<div className=' font-medium text-[#000D1D99] flex items-center'>
						{record.maxProfitRatio * 100}
						<span className='inline-block mx-1 text-[grey] text-[14px]'>%</span>
					</div>
				);
			},
		},
		{
			responsive: ['lg'],
			title: <ColumnTitle title={t<string>('paymentMethod')} />,
			dataIndex: 'paymentMethods',
			key: 'paymentMethods',
			render: (value, record) => (
				<span style={{ cursor: 'pointer' }} className='ml-5'>
					<Tooltip
						placement='bottomRight'
						title={
							<li>
								{record.paymentMethods.map((el) => (
									<span className='block'>{el.methodName}</span>
								))}
							</li>
						}
					>
						<EyeOutlined />
					</Tooltip>
				</span>
			),
			ellipsis: true,
		},
		{
			responsive: ['lg'],
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('public')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'is-public-asc'))}
							style={{ color: sortBy === 'is-public-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'is-public-desc'))}
							style={{ color: sortBy === 'is-public-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'isPublic',
			key: 'isPublic',
			render: (value, record) => {
				return (
					<div
						className='flex items-center  font-medium text-[#000]'
						style={{ color: record.isPublic ? 'green' : 'red' }}
					>
						{record.isPublic ? t<string>('yes') : t<string>('no')}
					</div>
				);
			},
		},
		{
			dataIndex: 'record',
			title: (
				<div>
					<Link to={`/add-country`}>
						<PlusOutlined className='flex items-center cursor-pointer  text-[20px] text-black font-bold' />
					</Link>
				</div>
			),
			className: 'small-screen-row-header',
			render: (value, row) => {
				return (
					<div className=' py-2 pb-0 justify-between'>
						<div className='flex flex-row py-2 justify-between'>
							<div>{t<string>('name')}:</div>
							<div className='text-xs text-[15px] font-medium text-lightBlue  flex items-center '>
								<Link to={`/countries/${row.countryCode}`}>
									<EditFilled className='p-3 flex items-center cursor-pointer  text-[20px] text-black font-bold' />
								</Link>
								{row.countryName}
							</div>
						</div>
						<div className='flex flex-row py-2 justify-between'>
							<div>{t<string>('countryCode')}:</div>
							<div className='text-xs text-[15px] font-medium text-lightBlue  flex items-center '>
								{row.countryCode}
							</div>
						</div>
						<div className='flex flex-row py-2 justify-between'>
							<div>{t<string>('currencyCode')}:</div>
							<div className='text-xs text-[15px] font-medium text-lightBlue  flex items-center '>
								{row.currencyCode}
							</div>
						</div>
						<div className='flex flex-row py-2 justify-between'>
							<div>{t<string>('maxProfitRatio')}:</div>
							<div className='text-xs text-[15px] font-medium text-lightBlue  flex items-center '>
								{row.maxProfitRatio * 100}%
							</div>
						</div>
						<div className='flex flex-row py-2 justify-between'>
							<div>{t<string>('public')}:</div>
							<div
								className='flex items-center  font-medium text-[#000]'
								style={{ color: row.isPublic ? 'green' : 'red' }}
							>
								{row.isPublic ? t<string>('yes') : t<string>('no')}
							</div>
						</div>
						<div className='flex flex-row py-2 justify-between'>
							<div>{t<string>('paymentMethod')}:</div>
							<div className='text-xs text-[15px] font-medium text-lightBlue  flex items-center '>
								<span style={{ cursor: 'pointer' }} className='ml-5'>
									<Tooltip
										placement='bottomRight'
										title={
											row.paymentMethods.length === 0 ? (
												<li>{t<string>('noPaymentMethod')}</li>
											) : (
												<li>
													{row.paymentMethods.map((el) => (
														<span className='block'>{el.methodName}</span>
													))}
												</li>
											)
										}
									>
										<EyeOutlined size={25} />
									</Tooltip>
								</span>
							</div>
						</div>
					</div>
				);
			},
		},
	];

	return columns;
};
